import { AssetClass } from '@/entities/assets'

import { TRANSACTION_TYPE } from '@/entities/transactions/utils/const'

import { WizardAccountMode, WizardCashFlow, WizardInvestor, WizardMark, WizardValues } from '../utils/types'
import { createHolding, ensureAccount, ensureTagIds, postTransaction } from '../utils/create'
import { useAssetPricesStore } from '@/store/assets/prices'

/**
 * One bundle per investor — an asset and a `subscribe and commit` transaction
 * each, held in that investor's own account, for that investor's own share —
 * plus whatever the cash-flows step logged against that same investor.
 *
 * The name is the fund's, the same for every bundle: it is one fund, however
 * many entities split the commitment. The ticker is what tells them apart —
 * `AssetClass.validate` requires one to be unique, and "Sequoia Growth III"
 * twice over would collide the moment a second investor joined.
 *
 * Sequential, not `Promise.all`: two investors can both resolve to the same
 * new account name — a family that invests through the same trust twice in
 * one sentence — and firing both creates at once would race to make it twice.
 */
export const create = async (values: WizardValues): Promise<AssetClass[]> => {
  const tagIds = await ensureTagIds(values.tags)
  const investors: WizardInvestor[] = values.investors ?? []
  const cashFlows: WizardCashFlow[] = values.cashFlows ?? []
  const totalShare = investors.reduce((sum, investor) => sum + (investor.share ?? 0), 0)

  const funds: AssetClass[] = []

  for (const [index, investor] of investors.entries()) {
    const accountId = await ensureAccount({
      id: investor.accountMode === WizardAccountMode.NEW ? undefined : investor.accountId,
      name: investor.accountName,
      type: investor.accountType,
      currency: values.currency,
    })

    const fund = await createHolding({
      accountId,
      transactionType: TRANSACTION_TYPE.SUBSCRIBE_AND_COMMIT,
      name: values.assetName,
      ticker: `${values.assetName} (${investor.accountName})`,
      type: values.assetType,
      currency: values.currency,
      description: values.description,
      amount: investor.share ?? 0,
      date: values.date,
      tagIds,
    })

    /*
     * The NAVs the valuation step recorded, pro-rated to this bundle's own
     * share of the commitment — the step asks for one NAV for the whole
     * investment, not one per investor, and posting that same absolute
     * number against every bundle would multiply the fund's value by however
     * many entities split it.
     */
    await createInvestorMarks(fund.id, values.prices, totalShare ? (investor.share ?? 0) / totalShare : 0)

    /*
     * This investor's own capital calls, fees and distributions — logged
     * against the bundle just created, in the same account, one call each so
     * a later one failing does not leave an earlier one half-posted.
     */
    await createInvestorCashFlows(
      accountId,
      fund.id,
      values.currency,
      cashFlows.filter(flow => flow.investorIndex === index),
    )

    funds.push(fund)
  }

  return funds
}

/** This investor's own rows from the cash-flows step, posted as follow-up
 * transactions against the bundle `create` just made for them. */
const createInvestorCashFlows = async (
  accountId: string,
  assetId: string,
  currency: string,
  flows: WizardCashFlow[],
): Promise<void> => {
  for (const flow of flows) {
    await postTransaction({
      accountId,
      assetId,
      transactionType: flow.type,
      currency,
      amount: flow.amount,
      date: flow.date,
    })
  }
}

/**
 * One investor's slice of the valuation step's own marks — see `create`'s
 * pro-rating note. `fraction` is this investor's share of the total, so the
 * sum of every investor's marks on a given date reconstructs the whole NAV
 * the step actually collected.
 */
const createInvestorMarks = async (
  assetId: string,
  marks: WizardMark[] = [],
  fraction: number,
): Promise<void> => {
  const pricesStore = useAssetPricesStore()

  for (const mark of marks) {
    await pricesStore.store({
      asset_id: assetId,
      date: mark.date,
      close: mark.close * fraction,
      source: mark.source,
    })
  }
}
